import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { PaperPlaneTilt, CheckCircle, LockOpen, Truck, Star, Download, Receipt } from '@phosphor-icons/react';
import DatePicker from '@/components/DatePicker';
import { toast } from 'sonner';
import InvoicesDialog from '@/components/InvoicesDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PurchaseOrdersPanel = () => {
  const { getAuthHeader } = useAuth();
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendVendorId, setSendVendorId] = useState('');
  const [busy, setBusy] = useState(false);
  const [deliveryTarget, setDeliveryTarget] = useState(null);
  const [delivery, setDelivery] = useState({ actual_delivery_date: '', delivery_status: '', quality_rating: '' });
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releaseCcEmails, setReleaseCcEmails] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [invoicesTarget, setInvoicesTarget] = useState(null);

  const load = async () => {
    try {
      const [poRes, vRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/vendors`, { headers: getAuthHeader() }),
      ]);
      setPos(poRes.data);
      setVendors(vRes.data);
    } catch (e) {
      toast.error('Failed to load purchase orders');
    }
  };

  useEffect(() => { load(); }, []);

  const handleRelease = async () => {
    if (!releaseTarget) return;
    setReleasing(true);
    try {
      const cc_emails = releaseCcEmails
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      await axios.post(
        `${API}/purchase-orders/${releaseTarget.id}/release`,
        { cc_emails, notes: releaseNotes.trim() || null },
        { headers: getAuthHeader() }
      );
      toast.success(`PO ${releaseTarget.po_number} marked as sent to vendor`);
      setReleaseTarget(null);
      setReleaseCcEmails('');
      setReleaseNotes('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to release PO');
    } finally {
      setReleasing(false);
    }
  };

  const handleDownloadPoPdf = async (po) => {
    try {
      const response = await axios.get(`${API}/purchase-orders/${po.id}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', po.po_file_name || `PO_${po.po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No signed PO document uploaded yet');
    }
  };

  const openSendDialog = (po) => {
    const match = vendors.find((v) => v.name.toLowerCase() === (po.vendor_name || '').toLowerCase());
    setSendVendorId(match ? match.id : '');
    setSendTarget(po);
  };

  const handleSend = async () => {
    if (!sendVendorId) { toast.error('Select a vendor from the directory'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/purchase-orders/${sendTarget.id}/send-to-vendor`,
        { vendor_id: sendVendorId }, { headers: getAuthHeader() });
      toast.success(`${res.data.message} (${res.data.sent_to.length} recipient${res.data.sent_to.length === 1 ? '' : 's'})`);
      setSendTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send PO');
    } finally {
      setBusy(false);
    }
  };

  const selectedVendor = vendors.find((v) => v.id === sendVendorId);

  const handleRecordDelivery = async () => {
    if (!delivery.actual_delivery_date) { toast.error('Actual delivery date is required'); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/purchase-orders/${deliveryTarget.id}/update-delivery`, {
        actual_delivery_date: delivery.actual_delivery_date,
        delivery_status: delivery.delivery_status || undefined,
        quality_rating: delivery.quality_rating ? parseInt(delivery.quality_rating) : undefined,
      }, { headers: getAuthHeader() });
      toast.success(`Delivery recorded for PO ${deliveryTarget.po_number} — requesters notified`);
      setDeliveryTarget(null);
      setDelivery({ actual_delivery_date: '', delivery_status: '', quality_rating: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record delivery');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-border-default rounded">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface border-b border-border-default">
            <tr>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">PO Number</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Vendor</th>
              <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Amount</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Status</th>
              <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pos.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-text-tertiary">No purchase orders yet.</td></tr>
            )}
            {pos.map((po) => (
              <tr key={po.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`po-panel-row-${po.id}`}>
                <td className="p-4 font-mono font-semibold text-text-primary">{po.po_number}</td>
                <td className="p-4 text-text-secondary">{po.vendor_name}</td>
                <td className="p-4 text-right font-semibold text-text-primary">₹{po.total_amount.toFixed(2)}</td>
                <td className="p-4">
                  <span className={`status-badge ${po.status === 'pending_release' ? 'status-pending' : 'status-approved'}`}>
                    {po.status.replace(/_/g, ' ')}
                  </span>
                  {po.sent_to_vendor_at && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-700" data-testid={`po-sent-indicator-${po.id}`}>
                      <CheckCircle size={14} weight="fill" />
                      Sent {new Date(po.sent_to_vendor_at).toLocaleDateString()}
                    </span>
                  )}
                </td>
                <td className="p-4 text-right">
                  <div className="flex gap-2 justify-end">
                    {po.po_file_id && (
                      <Button size="sm" variant="outline" onClick={() => handleDownloadPoPdf(po)} data-testid={`download-po-pdf-${po.id}`}>
                        <Download size={16} className="mr-1" weight="duotone" />
                        Download PO
                      </Button>
                    )}
                    {po.status === 'pending_release' && (
                      <Button size="sm" className="bg-accent-brand hover:bg-accent-brand-hover text-white" onClick={() => setReleaseTarget(po)} data-testid={`release-po-btn-${po.id}`}>
                        <LockOpen size={16} className="mr-1" /> Mark Sent to Vendor
                      </Button>
                    )}
                    {(po.status === 'released' || po.status === 'completed') && (
                      <Button size="sm" variant="outline" onClick={() => openSendDialog(po)} data-testid={`send-po-btn-${po.id}`}>
                        <PaperPlaneTilt size={16} className="mr-1" />
                        {po.sent_to_vendor_at ? 'Resend to Vendor' : 'Send to Vendor'}
                      </Button>
                    )}
                    {(po.status === 'released' || po.status === 'completed') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setInvoicesTarget(po)}
                        data-testid={`invoices-btn-${po.id}`}
                        title="Upload or view vendor invoices for this PO"
                      >
                        <Receipt size={16} className="mr-1" weight="duotone" />
                        Invoices
                      </Button>
                    )}
                    {(po.status === 'released' || po.status === 'completed') && (
                      po.actual_delivery_date ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled
                          className="text-emerald-700 border-emerald-300 bg-emerald-50 opacity-100"
                          title={`Delivered on ${new Date(po.actual_delivery_date).toLocaleDateString()}`}
                          data-testid={`delivered-btn-${po.id}`}
                        >
                          <CheckCircle size={16} className="mr-1" weight="fill" />
                          Delivered
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeliveryTarget(po)}
                          data-testid={`record-delivery-btn-${po.id}`}
                        >
                          <Truck size={16} className="mr-1" />
                          Record Delivery
                        </Button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sendTarget && (
        <Dialog open={!!sendTarget} onOpenChange={(open) => !open && setSendTarget(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Send PO {sendTarget.po_number} to Vendor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-text-secondary">
                The PO PDF will be attached and emailed to <strong>all</strong> contacts on file. Vendor replies go to your email.
              </p>
              <div className="space-y-2">
                <Select value={sendVendorId} onValueChange={setSendVendorId}>
                  <SelectTrigger data-testid="send-po-vendor-select">
                    <SelectValue placeholder="Select vendor from directory" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name} ({v.emails.length} contact{v.emails.length === 1 ? '' : 's'})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {vendors.length === 0 && (
                  <p className="text-xs text-amber-700">No vendors in directory yet — add one on the Vendors page first.</p>
                )}
                {selectedVendor && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-surface rounded" data-testid="send-po-recipients">
                    {selectedVendor.emails.map((em) => (
                      <span key={em} className="px-2 py-0.5 rounded-full text-xs bg-white border border-border-default text-text-secondary font-mono">{em}</span>
                    ))}
                  </div>
                )}
              </div>
              <Button className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white" disabled={busy || !sendVendorId} onClick={handleSend} data-testid="confirm-send-po-button">
                {busy ? 'Sending…' : 'Send PO with PDF Attachment'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {deliveryTarget && (
        <Dialog open={!!deliveryTarget} onOpenChange={(open) => !open && setDeliveryTarget(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Record Delivery — PO {deliveryTarget.po_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-text-secondary">
                Vendor: <strong>{deliveryTarget.vendor_name}</strong>
                {deliveryTarget.expected_delivery_date && (
                  <> · Expected: {new Date(deliveryTarget.expected_delivery_date).toLocaleDateString()}</>
                )}
                <br />Once recorded, delivery is locked for this PO and requesters are notified.
              </p>
              <div className="space-y-2">
                <Label>Actual Delivery Date</Label>
                <DatePicker
                  value={delivery.actual_delivery_date}
                  onChange={(v) => setDelivery({ ...delivery, actual_delivery_date: v })}
                  placeholder="Select delivery date"
                  testId="panel-actual-delivery-input"
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label>Delivery Status</Label>
                <Select value={delivery.delivery_status} onValueChange={(v) => setDelivery({ ...delivery, delivery_status: v })}>
                  <SelectTrigger data-testid="panel-delivery-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_time">On Time</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quality Rating</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setDelivery({ ...delivery, quality_rating: rating.toString() })}
                      className={`p-2 rounded transition-colors ${
                        parseInt(delivery.quality_rating) >= rating
                          ? 'text-status-warning'
                          : 'text-text-tertiary hover:text-status-warning'
                      }`}
                      data-testid={`panel-rating-star-${rating}`}
                    >
                      <Star size={24} weight={parseInt(delivery.quality_rating) >= rating ? 'fill' : 'regular'} />
                    </button>
                  ))}
                </div>
              </div>
              <Button
                className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white"
                disabled={busy || !delivery.actual_delivery_date}
                onClick={handleRecordDelivery}
                data-testid="confirm-record-delivery-button"
              >
                {busy ? 'Recording…' : 'Record Delivery'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Mark PO Sent to Vendor Dialog */}
      {releaseTarget && (
        <Dialog open={!!releaseTarget} onOpenChange={(open) => { if (!open && !releasing) { setReleaseTarget(null); setReleaseCcEmails(''); setReleaseNotes(''); }}}>
          <DialogContent className="sm:max-w-md" data-testid="release-po-dialog">
            <DialogHeader>
              <DialogTitle>Mark PO {releaseTarget.po_number} as sent to vendor?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="p-3 bg-surface rounded space-y-1">
                <div><span className="text-text-tertiary">Vendor:</span> <span className="font-semibold">{releaseTarget.vendor_name}</span></div>
                <div><span className="text-text-tertiary">Amount:</span> <span className="font-semibold">₹{Number(releaseTarget.total_amount || 0).toFixed(2)}</span></div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cc_emails">CC emails (optional)</Label>
                <Input
                  id="cc_emails"
                  value={releaseCcEmails}
                  onChange={(e) => setReleaseCcEmails(e.target.value)}
                  placeholder="finance@vendor.com, sales@vendor.com"
                  data-testid="release-cc-emails-input"
                />
                <p className="text-xs text-text-tertiary">Comma-separated. Recorded on the audit trail (not auto-emailed).</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="release_notes">Notes (optional)</Label>
                <textarea
                  id="release_notes"
                  value={releaseNotes}
                  onChange={(e) => setReleaseNotes(e.target.value)}
                  placeholder="e.g., sent by courier, tracking ID XYZ…"
                  className="w-full min-h-[60px] rounded border border-border-default px-3 py-2 text-sm"
                  data-testid="release-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setReleaseTarget(null)} disabled={releasing} data-testid="cancel-release-po-button">Cancel</Button>
                <Button className="bg-accent-brand hover:bg-accent-brand-hover text-white" onClick={handleRelease} disabled={releasing} data-testid="confirm-release-po-button">
                  <LockOpen size={16} className="mr-2" /> {releasing ? 'Marking…' : 'Mark as Sent'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Invoices Dialog */}
      <InvoicesDialog
        po={invoicesTarget}
        open={!!invoicesTarget}
        onClose={() => setInvoicesTarget(null)}
        onChanged={load}
      />
    </div>
  );
};

export default PurchaseOrdersPanel;
